/**
 * Overview: the period at a glance, unfiltered. Four bordered panels: the top
 * ten processes, models and people by spend, and the spend over time (per
 * day in a month, per month in a year). A name or a bar opens its runs;
 * "See all" opens the full Breakdown.
 */

import { ArrowRight } from 'lucide-react';

import type { RunGroupBy, Usage, UsageBucket } from '@/api/types';
import { formatCompact, formatInt, formatUsd, monthLabel } from '@/ui/format';

import { GroupName, daysOf, formatDay, groupInfo, isYear, periodLabel } from './usageUi';

/** How many rows each panel shows. */
const TOP = 10;

type Drill = (by: RunGroupBy, key: string) => void;
type SeeAll = (by: RunGroupBy) => void;

export function OverviewTab({ data, onDrill, onSeeAll }: { data: Usage; onDrill: Drill; onSeeAll: SeeAll }) {
  const when = periodLabel(data.month);
  return (
    <div className="usage-panels">
      <TopPanel title="By process" by="process" rows={data.by_process} empty={`No AI process ran in ${when}.`} onDrill={onDrill} onSeeAll={onSeeAll} />
      <TopPanel title="By model" by="model" rows={data.by_model} empty={`No model was called in ${when}.`} onDrill={onDrill} onSeeAll={onSeeAll} />
      <TopPanel title="By person" by="user" rows={data.by_user} empty={`Nobody triggered an AI call in ${when}.`} onDrill={onDrill} onSeeAll={onSeeAll} />
      {isYear(data.month) ? (
        <SpendChart
          title="Monthly spend"
          by="month"
          period={data.month}
          rows={data.by_month}
          columns={Array.from({ length: 12 }, (_, i) => `${data.month}-${String(i + 1).padStart(2, '0')}`)}
          name={(key) => monthLabel(key)}
          axis={['Jan', 'Jun', 'Dec']}
          onDrill={onDrill}
          onSeeAll={onSeeAll}
        />
      ) : (
        <SpendChart
          title="Daily spend"
          by="day"
          period={data.month}
          rows={data.by_day}
          columns={daysOf(data.month)}
          name={formatDay}
          axis={['1', String(Math.ceil(daysOf(data.month).length / 2)), String(daysOf(data.month).length)]}
          onDrill={onDrill}
          onSeeAll={onSeeAll}
        />
      )}
    </div>
  );
}

/** "See all 23 processes": the Breakdown by the same dimension. */
function SeeAllButton({ count, by, onSeeAll }: { count: number; by: RunGroupBy; onSeeAll: SeeAll }) {
  return (
    <button type="button" className="btn btn-ghost btn-sm usage-panel-more" onClick={() => onSeeAll(by)}>
      See all {formatInt(count)} {groupInfo(by).noun} <ArrowRight aria-hidden="true" />
    </button>
  );
}

/** The top rows by spend, each with a bar sized to the largest; a name opens its runs. */
function TopPanel({
  title,
  by,
  rows,
  empty,
  onDrill,
  onSeeAll,
}: {
  title: string;
  by: RunGroupBy;
  rows: UsageBucket[];
  empty: string;
  onDrill: Drill;
  onSeeAll: SeeAll;
}) {
  const top = rows.slice(0, TOP);
  const max = Math.max(...top.map((r) => Number(r.cost_usd)), 0);
  return (
    <section className="usage-panel" aria-label={title}>
      <div className="usage-panel-head">
        <h3>{title}</h3>
        {rows.length > TOP && <span className="hint">Top {TOP} of {formatInt(rows.length)}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="hint">{empty}</p>
      ) : (
        <table className="data compact">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col" className="num">Runs</th>
              <th scope="col" className="num">Tokens</th>
              <th scope="col" className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {top.map((r) => (
              <tr key={r.key}>
                <td>
                  <GroupName label={r.label} drillable={r.key !== '—'} onClick={() => onDrill(by, r.key)} />
                  <span className="bar-track" aria-hidden="true">
                    <span className="bar-fill" style={{ width: `${max > 0 ? (Number(r.cost_usd) / max) * 100 : 0}%` }} />
                  </span>
                </td>
                <td className="num">{formatInt(r.runs)}</td>
                <td className="num">{formatCompact(r.tokens_input + r.tokens_output)}</td>
                <td className="num">{formatUsd(r.cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > TOP && <SeeAllButton count={rows.length} by={by} onSeeAll={onSeeAll} />}
    </section>
  );
}

/**
 * One column per day (or month) of the period; single series, so the title
 * names it. A bar opens that day's or month's runs.
 */
function SpendChart({
  title,
  by,
  period,
  rows,
  columns,
  name,
  axis,
  onDrill,
  onSeeAll,
}: {
  title: string;
  by: 'day' | 'month';
  period: string;
  rows: UsageBucket[];
  /** Every column's key, in order: the days or months of the period. */
  columns: string[];
  name: (key: string) => string;
  /** Labels under the first, middle and last column. */
  axis: string[];
  onDrill: Drill;
  onSeeAll: SeeAll;
}) {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const max = Math.max(...rows.map((r) => Number(r.cost_usd)), 0);

  return (
    <section className="usage-panel" aria-label={title}>
      <div className="usage-panel-head">
        <h3>{title}</h3>
        {rows.length > 0 && <span className="hint">Peak {formatUsd(max)}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="hint">No spend recorded in {periodLabel(period)}.</p>
      ) : (
        <>
          <div className={`day-chart${by === 'month' ? ' is-months' : ''}`} role="group" aria-label={`${title} for ${periodLabel(period)}, peak ${formatUsd(max)}`}>
            {columns.map((key) => {
              const bucket = byKey.get(key);
              const cost = Number(bucket?.cost_usd ?? 0);
              const height = `${max > 0 ? Math.max((cost / max) * 100, cost > 0 ? 3 : 0) : 0}%`;
              return bucket ? (
                <button
                  key={key}
                  type="button"
                  className="day-col"
                  aria-label={`${name(key)}: ${formatUsd(cost)}, ${formatInt(bucket.runs)} runs. Show the runs.`}
                  onClick={() => onDrill(by, key)}
                >
                  <span className="day-bar" style={{ height }} />
                  <span className="day-tip" aria-hidden="true">
                    {name(key)}: <b>{formatUsd(cost)}</b> · {formatInt(bucket.runs)} run{bucket.runs === 1 ? '' : 's'}
                  </span>
                </button>
              ) : (
                <div key={key} className="day-col" aria-hidden="true" />
              );
            })}
          </div>
          <div className="day-axis hint">
            {axis.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <SeeAllButton count={rows.length} by={by} onSeeAll={onSeeAll} />
        </>
      )}
    </section>
  );
}
